/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { forwardRef } from 'react';

import { css, jsx } from '@compiled/react';

import { cssMap, cx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import { TH, type THProps } from '../../../ui/primitives/th';
import {
	cssPinningBackgroundLeft,
	cssPinningBackgroundRight,
	cssVarTableHeight,
} from '../../charlie-table-dnd-wrapper';
import { TableType } from '../../charlie-table.types';

const inventoryHeadStyles = cssMap({
	InventoryHead: {
		height: '48px',
		backgroundColor: token('elevation.surface'),
		borderBlockStart: `${token('border.width')} solid ${token('color.border')}`,
		borderBlockEnd: `${token('border.width.selected')} solid ${token('color.border')}`,
	},
});

const inputHeadStyles = cssMap({
	InputHeadStyle: {
		backgroundColor: token('elevation.surface.sunken'),
		borderBlockStart: `${token('border.width')} solid ${token('color.border')}`,
		borderBlockEnd: `${token('border.width')} solid ${token('color.border')}`,
		borderInlineEndStyle: 'solid',
		borderInlineEndWidth: token('border.width'),
	},
});

const headCellStyles = cssMap({
	HeadCell: {
		zIndex: 200,
		position: 'sticky',
		borderColor: token('color.border'),
		whiteSpace: 'nowrap',
		verticalAlign: 'middle',
		color: token('color.text.subtlest'),
		textAlign: 'left',
		top: token('space.0'),
		paddingInline: token('space.100'),
		display: 'flex',
		alignItems: 'center',
		borderInlineEndStyle: 'solid',
	},
});

const pinnedColumnLeftStyle = css({
	position: 'absolute',
	top: token('space.0'),
	right: token('space.0'),
	'&::after': {
		content: '""',
		position: 'absolute',
		right: token('space.negative.075'),
		top: token('space.0'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
		height: `var(${cssVarTableHeight})`,
		width: '6px',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
		background: `var(${cssPinningBackgroundLeft})`,
	},
});

const pinnedColumnRightStyle = css({
	position: 'absolute',
	top: token('space.0'),
	left: token('space.0'),
	'&::before': {
		content: '""',
		position: 'absolute',
		left: token('space.negative.075'),
		top: '0',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
		height: `var(${cssVarTableHeight})`,
		width: '6px',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
		background: `var(${cssPinningBackgroundRight})`,
	},
});

export type HeadCellProps = {
	tableType?: TableType;
	isPinningEdge?: 'right' | 'left';
} & THProps;

/**
 * __HeadCell__
 *
 * HeadCell element. A styled `th` element.
 */
export const HeadCell = forwardRef<HTMLTableCellElement, HeadCellProps>(
	(
		{
			children,
			align,
			testId,
			backgroundColor,
			xcss,
			scope = 'col',
			tableType = TableType.input,
			isPinningEdge,
			...props
		},
		ref,
	) => {
		return (
			<TH
				{...props}
				scope={scope}
				align={align}
				testId={testId}
				ref={ref}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
				xcss={cx(
					headCellStyles.HeadCell,
					tableType === TableType.inventory && inventoryHeadStyles.InventoryHead,
					tableType === TableType.input && inputHeadStyles.InputHeadStyle,
					xcss,
				)}
				backgroundColor={
					tableType === TableType.inventory
						? 'elevation.surface'
						: tableType === TableType.input
							? 'elevation.surface.sunken'
							: backgroundColor
				}
			>
				{children}
				{isPinningEdge && (
					<div
						css={[
							isPinningEdge === 'left' && pinnedColumnLeftStyle,
							isPinningEdge === 'right' && pinnedColumnRightStyle,
						]}
					/>
				)}
			</TH>
		);
	},
);
